
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import Workspace from './components/Workspace';
import Pricing from './components/Pricing';
import UpgradePage from './components/UpgradePage';
import LoginModal from './components/LoginModal';
import SignUpModal from './components/SignUpModal';
import InviteModal from './components/InviteModal';
import NewRecordingModal from './components/NewRecordingModal';
import ShareModal from './components/ShareModal';
import PublicShareView from './components/PublicShareView';
import GuestGateModal from './components/GuestGateModal';
import CofounderWelcomeModal from './components/CofounderWelcomeModal';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import EnterprisePage from './components/EnterprisePage';
import ApiPage from './components/ApiPage';
import AboutPage from './components/AboutPage';
import BlogPage from './components/BlogPage';
import CareersPage from './components/CareersPage';
import ContactPage from './components/ContactPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import SecurityPage from './components/SecurityPage';
import ChangelogPage from './components/ChangelogPage';
import AlertModal from './components/AlertModal';
import Navbar from './components/Navbar';
import { getUser, onKickOut, resetKickOutFlag, startHeartbeat, stopHeartbeat } from './services/api';

import { Monitor } from 'lucide-react';
import { LanguageProvider, useLanguage } from './i18nContext';
import { Agentation } from 'agentation';

const COFOUNDER_PENDING_KEY = 'cofounder-welcome-pending';

type PostSignupFlow = 'cofounder' | 'pricing' | null;

const getCofounderSeenKey = (userId: number | string) => `cofounder-welcome-seen:${userId}`;

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isNewRecordingOpen, setIsNewRecordingOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isGuestGateOpen, setIsGuestGateOpen] = useState(false);
  const [postSignupFlow, setPostSignupFlow] = useState<PostSignupFlow>(null);
  const [showWelcomePricing, setShowWelcomePricing] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [kickOutReason, setKickOutReason] = useState<'other_device' | 'session_expired' | null>(null);
  const [alertModal, setAlertModal] = useState<{ title: string; message: string; confirmText?: string; onConfirm?: () => void } | null>(null);
  const [returnToNewRecording, setReturnToNewRecording] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [membershipRefreshKey, setMembershipRefreshKey] = useState(0);
  const [inviteCode, setInviteCode] = useState<string>('');
  const [signUpPrefill, setSignUpPrefill] = useState<{ method: 'email' | 'phone'; contact: string; code: string } | null>(null);

  const { t, language, setLanguage } = useLanguage();

  // 页面加载时检�?token，未登录访问 /dashboard 等保护页面则跳回首页
  useEffect(() => {
    const token = localStorage.getItem('token');
    const protectedPaths = ['/dashboard', '/workspace'];
    const isProtected = protectedPaths.some(p => location.pathname.startsWith(p));
    // If it's the dashboard and guest gate is open, allow it even without token
    if (!token && isProtected) {
      if (location.pathname.startsWith('/dashboard') && isGuestGateOpen) {
        return;
      }
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, isGuestGateOpen]);

  // 检查URL参数中的邀请码
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('inviteCode');
    if (code) {
      setInviteCode(code);
      setIsSignUpOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // 监听全局踢出事件 + 心跳
  useEffect(() => {
    const unsubscribe = onKickOut((reason) => {
      setKickOutReason(reason);
      stopHeartbeat();
    });
    const token = localStorage.getItem('token');
    if (token) {
      startHeartbeat();
    }
    return () => {
      unsubscribe();
      stopHeartbeat();
    };
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith('/dashboard')) return;
    if (postSignupFlow) return;

    const pending = sessionStorage.getItem(COFOUNDER_PENDING_KEY);
    if (!pending) return;

    const currentUser = getUser();
    const userId = currentUser?.userId;
    if (!userId) return;

    if (localStorage.getItem(getCofounderSeenKey(userId)) === '1') {
      sessionStorage.removeItem(COFOUNDER_PENDING_KEY);
      return;
    }

    setPostSignupFlow('cofounder');
  }, [location.pathname, postSignupFlow]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'zh' : 'en');
  };

  const handleStartFreeTrial = () => {
    navigate('/dashboard');
    setIsGuestGateOpen(true);
  };

  const handleSignUpSuccess = () => {
    setIsSignUpOpen(false);
    setIsGuestGateOpen(false);
    setDashboardRefreshKey(k => k + 1);
    sessionStorage.setItem(COFOUNDER_PENDING_KEY, '1');
    setPostSignupFlow('cofounder');
    navigate('/dashboard');
    startHeartbeat();
  };

  const finishCofounderFlow = () => {
    const currentUser = getUser();
    const userId = currentUser?.userId;

    if (userId) {
      localStorage.setItem(getCofounderSeenKey(userId), '1');
    }

    sessionStorage.removeItem(COFOUNDER_PENDING_KEY);
    setPostSignupFlow(null);
  };

  const handleCloseCofounderWelcome = () => {
    finishCofounderFlow();
  };

  const handleViewCofounderPricing = () => {
    finishCofounderFlow();
    setShowWelcomePricing(true);
  };

  const isNavbarPage = location.pathname === '/' || location.pathname === '/pricing';

  return (
    <div className="min-h-screen bg-surface text-brand-900 font-sans selection:bg-surface-border selection:text-brand-800 relative z-0">

      {/* Premium Blurred Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-[-1]">
        <div className="absolute -top-[10%] -left-[10%] w-[50%] h-[50%] bg-brand-200/20 rounded-full blur-[100px] max-w-3xl"></div>
        <div className="absolute bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-prestige-300/15 rounded-full blur-[100px] max-w-3xl"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.025] mix-blend-overlay"></div>
      </div>

      {/* Global Navigation */}
      {isNavbarPage && (
        <Navbar
          navigateTo={(page) => navigate(`/${page === 'landing' ? '' : page}`)}
          toggleLanguage={toggleLanguage}
          setIsLoginOpen={setIsLoginOpen}
          setIsSignUpOpen={setIsSignUpOpen}
          t={t}
        />
      )}

      {/* Main Content Router */}
      <main className={isNavbarPage ? 'pt-24' : ''}>
        <Routes>
          <Route path="/" element={
            <LandingPage
              onStart={handleStartFreeTrial}
              onNavigate={(page) => navigate(`/${page === 'landing' ? '' : page}`)}
            />
          } />

          <Route path="/pricing" element={<Pricing onSignUp={() => setIsSignUpOpen(true)} />} />

          <Route path="/dashboard" element={
            <>
              <Dashboard
                key={dashboardRefreshKey}
                onOpenNewRecording={() => setIsNewRecordingOpen(true)}
                onOpenWorkspace={(taskId?: string) => {
                  setSelectedTaskId(taskId || null);
                  navigate(taskId ? `/workspace/${taskId}` : '/workspace');
                }}
                onLogout={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  sessionStorage.removeItem('tutorial-session-dismissed');
                  stopHeartbeat();
                  navigate('/');
                }}
                onUpgrade={() => setShowUpgrade(true)}
                onOpenInvite={() => setIsInviteOpen(true)}
                onOpenShare={() => setIsShareModalOpen(true)}
                membershipRefreshKey={membershipRefreshKey}
              />
              {isGuestGateOpen && (
                <GuestGateModal
                  onSignUp={() => setIsSignUpOpen(true)}
                  onSignIn={() => setIsLoginOpen(true)}
                />
              )}
              {postSignupFlow === 'cofounder' && (
                <CofounderWelcomeModal
                  onClose={handleCloseCofounderWelcome}
                  onViewPricing={handleViewCofounderPricing}
                />
              )}
              {showWelcomePricing && (
                <UpgradePage mode="welcome" onBack={() => { setShowWelcomePricing(false); setMembershipRefreshKey(k => k + 1); }} />
              )}
              {showUpgrade && (
                <UpgradePage onBack={() => {
                  setShowUpgrade(false);
                  setMembershipRefreshKey(k => k + 1);
                  if (returnToNewRecording) {
                    setReturnToNewRecording(false);
                    setIsNewRecordingOpen(true);
                  }
                }} />
              )}
            </>
          } />

          <Route path="/workspace/:taskId" element={
            <Workspace
              taskId={selectedTaskId}
              onBack={() => navigate('/dashboard')}
              onOpenShare={() => setIsShareModalOpen(true)}
            />
          } />
          <Route path="/workspace" element={
            <Workspace
              taskId={selectedTaskId}
              onBack={() => navigate('/dashboard')}
              onOpenShare={() => setIsShareModalOpen(true)}
            />
          } />

          <Route path="/share/:shareId" element={
            <PublicShareView
              onSignUp={() => setIsSignUpOpen(true)}
              onLogin={() => setIsLoginOpen(true)}
            />
          } />

          <Route path="/enterprise" element={<EnterprisePage onBack={() => navigate('/')} />} />
          <Route path="/api" element={<ApiPage onBack={() => navigate('/')} />} />
          <Route path="/about" element={<AboutPage onBack={() => navigate('/')} />} />
          <Route path="/blog" element={<BlogPage onBack={() => navigate('/')} />} />
          <Route path="/careers" element={<CareersPage onBack={() => navigate('/')} />} />
          <Route path="/contact" element={<ContactPage onBack={() => navigate('/')} />} />
          <Route path="/privacy" element={<PrivacyPage onBack={() => navigate('/')} />} />
          <Route path="/terms" element={<TermsPage onBack={() => navigate('/')} />} />
          <Route path="/security" element={<SecurityPage onBack={() => navigate('/')} />} />
          <Route path="/changelog" element={<ChangelogPage onBack={() => navigate('/')} />} />

          {/* 404 fallback */}
          <Route path="*" element={
            <LandingPage
              onStart={handleStartFreeTrial}
              onNavigate={(page) => navigate(`/${page === 'landing' ? '' : page}`)}
            />
          } />
        </Routes>
      </main>

      {/* Modals */}
      {isLoginOpen && (
        <LoginModal
          onClose={() => setIsLoginOpen(false)}
          onLogin={() => {
            setIsLoginOpen(false);
            setIsGuestGateOpen(false);
            setDashboardRefreshKey(k => k + 1);
            navigate('/dashboard');
            startHeartbeat();
          }}
          onSwitchToSignUp={(prefill) => { setIsLoginOpen(false); setSignUpPrefill(prefill || null); setIsSignUpOpen(true); }}
          onForgotPassword={() => { setIsLoginOpen(false); setIsForgotPasswordOpen(true); }}
          onNavigate={(page) => { setIsLoginOpen(false); navigate(`/${page === 'landing' ? '' : page}`); }}
        />
      )}

      {isSignUpOpen && (
        <SignUpModal
          onClose={() => { setIsSignUpOpen(false); setInviteCode(''); setSignUpPrefill(null); }}
          onSignUp={handleSignUpSuccess}
          onSwitchToLogin={() => { setIsSignUpOpen(false); setIsLoginOpen(true); setInviteCode(''); setSignUpPrefill(null); }}
          initialInviteCode={inviteCode}
          prefill={signUpPrefill}
        />
      )}

      {isInviteOpen && <InviteModal onClose={() => setIsInviteOpen(false)} />}

      {isNewRecordingOpen && (
        <NewRecordingModal
          onClose={() => setIsNewRecordingOpen(false)}
          onStart={async (params) => {
            console.log('Task params:', params);
            if (!params.fileId) {
              console.error('No fileId provided');
              return;
            }
            try {
              const { createDraft } = await import('./services/task');
              const draftResponse = await createDraft({
                fileId: params.fileId,
                industry: params.industry,
                company: params.company,
                context: params.context,
                scene: params.scene,
              });
              console.log('Task created:', draftResponse.taskId);
              setIsNewRecordingOpen(false);
              setSelectedTaskId(null);
              setDashboardRefreshKey(prev => prev + 1);
              navigate('/dashboard');
            } catch (error: any) {
              console.error('Failed to create task:', error);
              if (error.message && error.message.includes('INSUFFICIENT_QUOTA')) {
                setAlertModal({
                  title: language === 'en' ? 'Insufficient Quota' : '时长配额不足',
                  message: language === 'en' ? 'Your quota is insufficient. Please upgrade your plan or purchase a duration package.' : 'ʱ�����㣬��������Ա����ʱ������',
                  confirmText: language === 'en' ? 'Go Upgrade' : 'ȥ����',
                  onConfirm: () => {
                    setAlertModal(null);
                    setIsNewRecordingOpen(false);
                    setReturnToNewRecording(true);
                    setShowUpgrade(true);
                  },
                });
              } else {
                setAlertModal({
                  title: language === 'en' ? 'Task Creation Failed' : '创建任务失败',
                  message: error.message || (language === 'en' ? 'Failed to create task' : '创建任务失败'),
                });
              }
            }
          }}
        />
      )}

      {isShareModalOpen && (
        <ShareModal
          onClose={() => setIsShareModalOpen(false)}
          onPreviewPublicLink={() => {
            setIsShareModalOpen(false);
            navigate('/share/preview');
          }}
        />
      )}

      {isForgotPasswordOpen && (
        <ForgotPasswordModal
          onClose={() => setIsForgotPasswordOpen(false)}
          onBackToLogin={() => { setIsForgotPasswordOpen(false); setIsLoginOpen(true); }}
        />
      )}

      {alertModal && (
        <AlertModal
          title={alertModal.title}
          message={alertModal.message}
          confirmText={alertModal.confirmText}
          onClose={() => setAlertModal(null)}
          onConfirm={alertModal.onConfirm}
        />
      )}

      {kickOutReason && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <Monitor className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-brand-900 mb-2">
              {kickOutReason === 'other_device' ? t.dashboard.sessionKickedTitle : t.dashboard.sessionExpiredTitle}
            </h3>
            <p className="text-sm text-ink-secondary mb-6">
              {kickOutReason === 'other_device' ? t.dashboard.sessionKickedMessage : t.dashboard.sessionExpiredMessage}
            </p>
            <button
              onClick={() => {
                setKickOutReason(null);
                resetKickOutFlag();
                navigate('/');
                setIsLoginOpen(true);
              }}
              className="w-full py-3 bg-brand-900 text-white rounded-full font-semibold hover:bg-brand-800 transition-colors"
            >
              {t.dashboard.sessionConfirm}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppContent />
          {process.env.NODE_ENV === 'development' && <Agentation />}
        </BrowserRouter>
      </LanguageProvider>
    </HelmetProvider>
  );
}



